/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { Colors } from 'library';
import { BaseButton } from './BaseButton';

type CardPadding = 'xs' | 'sm' | 'md' | 'lg';
export type CardTone = 'default' | 'muted' | 'error' | 'warning' | 'success' | 'secondary';

interface CardBaseProps {
    id?: string;
    children: React.ReactNode;
    /** Inner padding: `sm` (16px), `md` (24px, default), or `lg` (32px). */
    padding?: CardPadding;
    /** Background tone: `default` (white), `muted` (faintGrey), `error` (lightRed + mediumRed border), `warning` (lightWarning + warning border), or `success` (successBackground + success border). */
    tone?: CardTone;
    /** If true, adds hover state (shadow + border tint). Automatically true when `to` or `onClick` is provided. */
    interactive?: boolean;
    /** If true, stretches to 100% of parent height (useful in grid/kanban layouts). */
    fullHeight?: boolean;
    /** If true, applies `flex: 1` so the card grows to fill available space next to flex siblings. */
    grow?: boolean;
    className?: string;
}

interface CardLinkProps extends CardBaseProps {
    /** If provided, renders the card as a react-router Link. */
    to: string;
    href?: never;
    onClick?: never;
    disabled?: never;
}

interface CardExternalLinkProps extends CardBaseProps {
    /** If provided, renders the card as an external anchor that opens in a new tab. */
    href: string;
    to?: never;
    onClick?: never;
    disabled?: never;
}

interface CardButtonProps extends CardBaseProps {
    to?: undefined;
    href?: undefined;
    /** If provided (or `interactive` is true), the card renders as a `<button>` element. */
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    /** Only applies when the card renders as a button. */
    disabled?: boolean;
}

interface CardStaticProps extends CardBaseProps {
    to?: undefined;
    href?: undefined;
    onClick?: undefined;
    interactive?: false;
    disabled?: never;
}

type CardProps = CardLinkProps | CardExternalLinkProps | CardButtonProps | CardStaticProps;

const PADDING_PX: Record<CardPadding, number> = { xs: 12, sm: 16, md: 24, lg: 32 };
const TONE_BG: Record<CardTone, string> = {
    default: Colors.white,
    muted: Colors.faintGrey,
    error: Colors.errorBackground,
    warning: Colors.warningBackground,
    success: Colors.successBackground,
    secondary: Colors.secondarySoft,
};
const TONE_BORDER: Record<CardTone, string> = {
    default: Colors.lightGrey,
    muted: Colors.lightGrey,
    error: Colors.error,
    warning: Colors.warning,
    success: Colors.success,
    secondary: Colors.secondary,
};
const TONE_HOVER_BORDER: Record<CardTone, string> = {
    default: Colors.surface,
    muted: Colors.fill,
    error: Colors.errorBackground,
    warning: Colors.warningBackground,
    success: Colors.successBackground,
    secondary: Colors.secondarySoft,
};

type CardStyleProps = {
    $padding: CardPadding;
    $tone: CardTone;
    $interactive: boolean;
    $fullHeight: boolean;
    $grow: boolean;
};

const cardStyles = ({ $padding, $tone, $interactive, $fullHeight, $grow }: CardStyleProps) => ({
    display: 'block',
    backgroundColor: TONE_BG[$tone],
    border: `1px solid ${TONE_BORDER[$tone]}`,
    borderRadius: 12,
    padding: PADDING_PX[$padding],
    boxSizing: 'border-box' as const,
    height: $fullHeight ? '100%' : undefined,
    flex: $grow ? 1 : undefined,
    color: 'inherit',
    textDecoration: 'none',
    transition: $interactive ? 'box-shadow 0.2s ease, border-color 0.2s ease' : undefined,
    cursor: $interactive ? ('pointer' as const) : undefined,
    '&:hover': $interactive
        ? {
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              borderColor: TONE_HOVER_BORDER[$tone],
              textDecoration: 'none',
          }
        : undefined,
});

const shouldForwardCardProp = (prop: string) =>
    !['$padding', '$tone', '$interactive', '$fullHeight', '$grow'].includes(prop);

const DivCard = styled('div', { shouldForwardProp: (p) => shouldForwardCardProp(p as string) })<CardStyleProps>(
    cardStyles,
);
const ButtonCard = styled(BaseButton, {
    shouldForwardProp: (p) => shouldForwardCardProp(p as string),
})<CardStyleProps>((props) => ({
    ...cardStyles(props),
    textAlign: 'left' as const,
    width: '100%',
}));
const LinkCard = styled(Link, { shouldForwardProp: (p) => shouldForwardCardProp(p as string) })<CardStyleProps>(
    cardStyles,
);
const AnchorCard = styled('a', { shouldForwardProp: (p) => shouldForwardCardProp(p as string) })<CardStyleProps>(
    cardStyles,
);

export const Card = React.forwardRef<HTMLElement, CardProps>(function Card(props, ref) {
    const {
        id,
        children,
        padding = 'md',
        tone = 'default',
        interactive,
        fullHeight = false,
        grow = false,
        className,
    } = props;

    if (props.href !== undefined) {
        return (
            <AnchorCard
                id={id}
                ref={ref as React.Ref<HTMLAnchorElement>}
                href={props.href}
                target="_blank"
                rel="noopener noreferrer"
                $padding={padding}
                $tone={tone}
                $interactive
                $fullHeight={fullHeight}
                $grow={grow}
                className={className}
            >
                {children}
            </AnchorCard>
        );
    }

    if (props.to !== undefined) {
        return (
            <LinkCard
                id={id}
                ref={ref as React.Ref<HTMLAnchorElement>}
                to={props.to}
                $padding={padding}
                $tone={tone}
                $interactive
                $fullHeight={fullHeight}
                $grow={grow}
                className={className}
            >
                {children}
            </LinkCard>
        );
    }

    const isButton = Boolean(props.onClick || interactive);
    if (isButton) {
        return (
            <ButtonCard
                id={id}
                ref={ref as React.Ref<HTMLButtonElement>}
                type="button"
                onClick={props.onClick}
                disabled={props.disabled}
                $padding={padding}
                $tone={tone}
                $interactive
                $fullHeight={fullHeight}
                $grow={grow}
                className={className}
            >
                {children}
            </ButtonCard>
        );
    }

    return (
        <DivCard
            id={id}
            ref={ref as React.Ref<HTMLDivElement>}
            $padding={padding}
            $tone={tone}
            $interactive={false}
            $fullHeight={fullHeight}
            $grow={grow}
            className={className}
        >
            {children}
        </DivCard>
    );
});
/* eslint-enable no-comments/disallowComments */
